import { Grid, Box, MenuItem } from '@mui/material';
import { nitrogenProtectant } from '@terraframing-suite/rx-config';
import { useEffect, useMemo } from 'react';
import { Radio, NumericInput, Select } from '../form-controls.jsx';
import { InputsHeader } from '../InputsHeader.jsx';
import { useContext } from '../context.jsx';
import useHasChanged from '../../../hooks/useHasChanged.js';

const disabledProducts = ['instinct', 'trident'];

export function NitrogenProtectantInputs() {
  const { getParam, updateParam } = useContext();
  const isFallEarlySpring =
    getParam('nitrogenProtectant.prodTime') === 'fall/early spring';

  const selectedTiming = getParam('nitrogenProtectant.prodTime');
  const timingChanged = useHasChanged(selectedTiming);

  const selectedProduct = getParam('nitrogenProtectant.prodUtilized');
  const productChanged = useHasChanged(selectedProduct);

  const rateIncrements = useMemo(() => {
    const product = nitrogenProtectant.products.find(
      (p) => p.id === selectedProduct,
    );

    if (!product?.increments) return [];

    return product.increments.map((increment) => (
      <MenuItem key={increment} value={increment}>
        {increment}
      </MenuItem>
    ));
  }, [selectedProduct]);

  useEffect(() => {
    const isInvalidProduct = disabledProducts.includes(selectedProduct);

    if (isFallEarlySpring && isInvalidProduct) {
      updateParam('nitrogenProtectant.prodUtilized', '');
    }
  }, [selectedProduct, isFallEarlySpring, updateParam]);

  useEffect(() => {
    if (!timingChanged && !productChanged) return;
    const rate = nitrogenProtectant.rates?.[selectedProduct]?.[selectedTiming];
    updateParam('nitrogenProtectant.baseRate', rate?.baseRate || '');
    updateParam('nitrogenProtectant.minRate', rate?.minRate || '');
    updateParam('nitrogenProtectant.npRateIncrement', rate?.increment || '');
  }, [
    productChanged,
    selectedProduct,
    selectedTiming,
    timingChanged,
    updateParam,
  ]);

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      {getRadioButtons(
        nitrogenProtectant.timings,
        'Select Application Timing',
        'nitrogenProtectant.prodTime',
      )}

      {getRadioButtons(
        nitrogenProtectant.products,
        'Select Product Utilized',
        'nitrogenProtectant.prodUtilized',
      )}

      <Grid item xs={6} sm={4}>
        <NumericInput
          name="nitrogenProtectant.baseRate"
          label="Product Base Rate (oz)"
          disabled
          required
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <NumericInput
          name="nitrogenProtectant.minRate"
          label="Min Application Rate (oz)"
          required
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <Select
          name="nitrogenProtectant.npRateIncrement"
          label="Rate Increment (oz)"
          options={rateIncrements}
          required
        />
      </Grid>
    </Grid>
  );

  function getRadioButtons(items, label, name) {
    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <InputsHeader subtitle={label} name={name} required />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          {items.map((item, i) => (
            <Box key={i} sx={{ display: 'flex' }}>
              <Radio
                value={item.id}
                name={name}
                label={item.label}
                disabled={
                  isFallEarlySpring && disabledProducts.includes(item.id)
                }
                onChange={() => updateParam(name, item.id)}
                color="inherit"
                sx={{
                  wordBreak: 'no-wrap',
                  whiteSpace: 'normal',
                }}
              />
            </Box>
          ))}
        </Box>
      </Grid>
    );
  }
}
